import React, { useEffect, useState } from "react";
import ModalCloseBtn from "@/components/ModalCloseBtn";
import { Plus, Trash2, Edit2, X, Upload, Download, FileSpreadsheet, Link2, RefreshCw, Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api, { BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";

export default function CommitteesTab({ event }) {
  const [committees, setCommittees] = useState([]);
  const { user } = useAuth();
  const [matrixToken, setMatrixToken] = useState(event?.matrix_token || "");
  const [matrixBusy, setMatrixBusy] = useState(false);
  const canManageMatrixLink =
    user &&
    (user.role === "admin" ||
      user.role === "regional_pm" ||
      (user.role === "project_manager" && event?.pm_id === user.user_id));
  const publicMatrixUrl = matrixToken ? `${window.location.origin}/matrix/${matrixToken}` : "";

  const generateOrRotateMatrix = async (rotate = false) => {
    if (rotate && !window.confirm("Rotating will revoke the OLD link — anyone with it will get a 404. Continue?")) return;
    setMatrixBusy(true);
    try {
      const r = await api.post(`/events/${event.event_id}/matrix-link`, { rotate });
      setMatrixToken(r.data.matrix_token);
      toast.success(rotate ? "New link generated — old one revoked" : "Matrix link generated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setMatrixBusy(false); }
  };

  const revokeMatrix = async () => {
    if (!window.confirm("Revoke the matrix link entirely? Anyone with the URL will get a 404.")) return;
    setMatrixBusy(true);
    try {
      await api.delete(`/events/${event.event_id}/matrix-link`);
      setMatrixToken("");
      toast.success("Matrix link revoked");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setMatrixBusy(false); }
  };

  const copyMatrixUrl = async () => {
    try {
      await navigator.clipboard.writeText(publicMatrixUrl);
      toast.success("Link copied to clipboard");
    } catch { toast.error("Copy failed — please copy manually"); }
  };

  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: "", agenda: "", matrix: "" });
  const [editId, setEditId] = useState(null);
  const [editDraft, setEditDraft] = useState({ name: "", agenda: "", matrix: "" });

  const load = async () => {
    const r = await api.get(`/events/${event.event_id}/committees`);
    setCommittees(r.data);
  };
  useEffect(() => { load(); }, [event.event_id]);

  const add = async (e) => {
    e.preventDefault();
    const matrix = form.matrix.split(/[,\n]/).map((s) => s.trim()).filter(Boolean);
    await api.post(`/events/${event.event_id}/committees`, { name: form.name, agenda: form.agenda, matrix });
    toast.success("Committee added");
    setShowAdd(false); setForm({ name: "", agenda: "", matrix: "" });
    load();
  };

  const startEdit = (c) => {
    setEditId(c.committee_id);
    setEditDraft({ name: c.name, agenda: c.agenda || "", matrix: (c.matrix || []).join("\n") });
  };

  const saveEdit = async (com) => {
    const matrix = editDraft.matrix.split(/[,\n]/).map((s) => s.trim()).filter(Boolean);
    try {
      await api.patch(`/committees/${com.committee_id}`, {
        name: editDraft.name.trim() || com.name,
        agenda: editDraft.agenda,
        matrix,
      });
      toast.success("Committee updated");
      setEditId(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    }
  };

  const del = async (com) => {
    if (!window.confirm(`Delete ${com.name}? All allotments will be removed.`)) return;
    await api.delete(`/committees/${com.committee_id}`);
    load();
  };

  const exportMatrix = (com) => {
    const token = localStorage.getItem("munners_token");
    window.open(`${BACKEND_URL}/api/committees/${com.committee_id}/matrix.xlsx?t=${token}`, "_blank");
  };

  const importMatrix = async (com, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post(`/committees/${com.committee_id}/matrix/import`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Matrix imported");
      load();
    } catch (e) {
      toast.error("Import failed: " + (e?.response?.data?.detail || e.message));
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-display font-bold text-xl">Committees & Country Matrices</h3>
          <p className="text-sm text-slate-500">Add committees, set agendas, define portfolios (countries). Everything can be edited later.</p>
        </div>
        <button onClick={() => setShowAdd(true)} className="btn-primary" data-testid="add-committee-btn"><Plus className="w-4 h-4" /> Add Committee</button>
      </div>

      {canManageMatrixLink && (
        <div className="dashboard-card p-4 mb-4 border-emerald-200 bg-emerald-50/40" data-testid="matrix-link-panel">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-center gap-2 font-display font-bold text-slate-900"><Link2 className="w-4 h-4 text-emerald-600" /> Public Matrix Link</div>
              <p className="text-xs text-slate-500 mt-0.5">Share this link with schools/delegates. Allotted portfolios show <span className="text-emerald-700 font-semibold">green</span>, open ones stay grey. No names are exposed.</p>
            </div>
            {!matrixToken ? (
              <button onClick={() => generateOrRotateMatrix(false)} disabled={matrixBusy}
                      className="btn-primary text-sm" data-testid="matrix-generate-btn">
                <Link2 className="w-4 h-4" /> Generate link
              </button>
            ) : (
              <div className="flex items-center gap-1.5 flex-wrap">
                <a href={publicMatrixUrl} target="_blank" rel="noreferrer"
                   className="btn-ghost text-sm inline-flex items-center gap-1" data-testid="matrix-open-btn">
                  <ExternalLink className="w-3.5 h-3.5" /> Open
                </a>
                <button onClick={copyMatrixUrl} className="btn-ghost text-sm inline-flex items-center gap-1" data-testid="matrix-copy-btn"><Copy className="w-3.5 h-3.5" /> Copy</button>
                <button onClick={() => generateOrRotateMatrix(true)} disabled={matrixBusy}
                        className="btn-ghost text-sm inline-flex items-center gap-1 text-amber-700" title="Revoke old link + generate new one"
                        data-testid="matrix-rotate-btn"><RefreshCw className="w-3.5 h-3.5" /> Rotate</button>
                <button onClick={revokeMatrix} disabled={matrixBusy}
                        className="btn-ghost text-sm inline-flex items-center gap-1 text-rose-600" data-testid="matrix-revoke-btn"><X className="w-3.5 h-3.5" /> Revoke</button>
              </div>
            )}
          </div>
          {matrixToken && (
            <div className="mt-3 text-xs font-mono text-slate-700 bg-white/70 border border-slate-200 rounded-lg px-3 py-2 break-all" data-testid="matrix-url-display">
              {publicMatrixUrl}
            </div>
          )}
        </div>
      )}

      <div className="space-y-4">
        {committees.length === 0 && <div className="dashboard-card p-12 text-center text-slate-400">No committees yet.</div>}
        {committees.map((c) => (
          <div key={c.committee_id} className="dashboard-card p-5" data-testid={`committee-${c.committee_id}`}>
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold text-lg text-slate-900">{c.name}</div>
                <div className="text-sm text-slate-500 mt-1"><span className="font-semibold text-slate-600">Agenda:</span> {c.agenda || <span className="text-slate-400">— not set —</span>}</div>
                <div className="text-xs text-slate-400 mt-1">{(c.matrix || []).length} portfolios</div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => exportMatrix(c)} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500" title="Download matrix" data-testid={`export-matrix-${c.committee_id}`}><FileSpreadsheet className="w-4 h-4" /></button>
                <label className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 cursor-pointer" title="Upload matrix">
                  <Upload className="w-4 h-4" />
                  <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={(e) => importMatrix(c, e.target.files?.[0])} />
                </label>
                <button onClick={() => startEdit(c)} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500" title="Edit committee" data-testid={`edit-committee-${c.committee_id}`}><Edit2 className="w-4 h-4" /></button>
                <button onClick={() => del(c)} className="p-2 rounded-lg hover:bg-rose-50 text-rose-500" title="Delete committee"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            {editId === c.committee_id && (
              <div className="mt-4 space-y-3 p-4 rounded-xl bg-orange-50/50 border border-orange-100" data-testid={`edit-form-${c.committee_id}`}>
                <div>
                  <label className="text-xs text-slate-500">Committee name</label>
                  <input value={editDraft.name} onChange={(e) => setEditDraft({ ...editDraft, name: e.target.value })} className="input-pill mt-1" data-testid={`edit-name-${c.committee_id}`} />
                </div>
                <div>
                  <label className="text-xs text-slate-500">Agenda</label>
                  <textarea rows={3} value={editDraft.agenda} onChange={(e) => setEditDraft({ ...editDraft, agenda: e.target.value })} className="input-pill mt-1" placeholder="e.g. Territorial disputes in the South China Sea" data-testid={`edit-agenda-${c.committee_id}`} />
                </div>
                <div>
                  <label className="text-xs text-slate-500">Portfolios (one per line or comma-separated)</label>
                  <textarea rows={6} value={editDraft.matrix} onChange={(e) => setEditDraft({ ...editDraft, matrix: e.target.value })} className="input-pill mt-1" placeholder="USA, UK, Russia, China…" data-testid={`edit-matrix-${c.committee_id}`} />
                </div>
                <div className="flex gap-2">
                  <button onClick={() => saveEdit(c)} className="btn-primary py-2 text-sm" data-testid={`save-committee-${c.committee_id}`}>Save changes</button>
                  <button onClick={() => setEditId(null)} className="btn-ghost py-2 text-sm">Cancel</button>
                </div>
              </div>
            )}
            {editId !== c.committee_id && (c.matrix || []).length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {c.matrix.map((p) => <span key={p} className="text-xs px-2 py-1 rounded-full bg-orange-50 text-orange-700 font-semibold">{p}</span>)}
              </div>
            )}
          </div>
        ))}
      </div>

      {showAdd && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full relative">
            <ModalCloseBtn onClose={() => setShowAdd(false)} />
            <h3 className="font-display font-black text-2xl mb-4">Add Committee</h3>
            <form onSubmit={add} className="space-y-3">
              <input required placeholder="Committee name (e.g., UNGA, UNSC)" className="input-pill" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="committee-name" />
              <textarea rows={2} placeholder="Agenda" className="input-pill" value={form.agenda} onChange={(e) => setForm({ ...form, agenda: e.target.value })} data-testid="committee-agenda" />
              <textarea rows={5} placeholder="Portfolios (one per line, e.g., USA, UK, China)" className="input-pill" value={form.matrix} onChange={(e) => setForm({ ...form, matrix: e.target.value })} data-testid="committee-matrix" />
              <button type="submit" className="btn-primary w-full py-3" data-testid="committee-submit">Add</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
